import { Component, EventEmitter, Output } from '@angular/core';
import { CenterNavigationItem, UserNavigationItem, UserSwitcherData } from '@vendasta/atlas';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AtlasConfigService, AtlasDataService } from '../../../core/src';
import { CustomUserDropdownItem } from '../user-dropdown/user-dropdown.interface';
import { AtlasUserLanguageModalService } from '../user-language-modal/user-language-modal.service';
import { GetByAccountGroupIdSetting } from '../vbc_sdk/_internal/objects/notifications';
import { NotificationsService } from '../vbc_sdk/notifications.service';
import { AtlasTopBarService } from './top-bar.service';

@Component({
  selector: 'atlas-top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss', '../common.scss'],
  providers: [AtlasUserLanguageModalService],
})
export class AtlasTopBarComponent {
  @Output() event: EventEmitter<string> = new EventEmitter<string>();

  centerData$: Observable<CenterNavigationItem[]>;
  userNavigationItems$: Observable<UserNavigationItem[]>;
  username$: Observable<string>;
  impersonateeUsername$: Observable<string | undefined>;
  partnerId$: Observable<string>;
  email$: Observable<string>;
  userId$: Observable<string>;
  signOutUrl$: Observable<string>;
  hideCenters$: Observable<boolean>;
  hideUserDropdown$: Observable<boolean>;
  showLanguageSelector$: Observable<boolean>;
  serviceProviderId$: Observable<string>;
  dropdownItems$: Observable<CustomUserDropdownItem[]>;
  notificationsEnabled$: Observable<boolean>;
  userSwitcherData$: Observable<UserSwitcherData[]>;
  partnerName$: Observable<string>;

  constructor(
    private dataService: AtlasDataService,
    private topbarService: AtlasTopBarService,
    private configService: AtlasConfigService,
    private notificationsService: NotificationsService,
    private userLanguageModalService: AtlasUserLanguageModalService,
  ) {
    this.centerData$ = this.dataService.centerData$;
    this.userNavigationItems$ = this.dataService.userData$;
    this.username$ = this.dataService.username$;
    this.impersonateeUsername$ = this.dataService.impersonateeUsername$;
    this.email$ = this.dataService.email$;
    this.userId$ = this.dataService.userId$;
    this.signOutUrl$ = this.dataService.signoutUrl$;
    this.userSwitcherData$ = this.dataService.userSwitcherData$;
    this.partnerName$ = this.dataService.partnerName$;

    // Checks if the user only has VBC. This is a special case for notifications because users who only use VBC will
    // need to have a check performed to see if their VBC notifications are turned off.
    const onlyVBCAccess$: Observable<boolean> = combineLatest([
      this.configService.accountId$,
      this.dataService.centerData$,
    ]).pipe(
      map(
        ([accountGroupId, centerData]: [string, CenterNavigationItem[]]) =>
          !!accountGroupId &&
          (centerData ? !centerData.find((center: CenterNavigationItem) => center.centerId !== 'VBC') : false),
      ),
      distinctUntilChanged(),
    );
    // If the state contains an account group id, we need to get the notifications settings to see if they are enabled
    // or not in Partner Center.
    const vbcNotificationSettings$: Observable<GetByAccountGroupIdSetting[]> = combineLatest(
      this.configService.partnerId$.pipe(distinctUntilChanged()),
      this.configService.accountId$.pipe(distinctUntilChanged()),
    ).pipe(
      switchMap(([partnerId, accountGroupId]: [string, string]) => {
        if (!!partnerId && !!accountGroupId) {
          return this.notificationsService
            .getByAccountGroupId$(partnerId, accountGroupId)
            .pipe(catchError(() => of(null)));
        }
        return of(null);
      }),
      map((resp) => (!!resp && !!resp.data ? resp.data : [])),
      // If there is not host defined for VBC, there is potential for getByAccountGroupId$ to never emit a value.
      // this will prevent notificationsEnabled$ combine latest from doing anything because it's going to sit there
      // waiting until this observable emits a value. In the case that there is not host set, we will assume there is
      // no VBC data to look at.
      startWith([]),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const vbcNotificationsEnabled$: Observable<boolean> = combineLatest(
      this.dataService.userId$,
      vbcNotificationSettings$,
    ).pipe(
      map(([userId, settings]: [string, GetByAccountGroupIdSetting[]]) =>
        settings ? settings.find((setting) => !!setting && setting.unifiedUserId === userId) : null,
      ),
      map((setting: GetByAccountGroupIdSetting): boolean => !!setting && !!setting.notificationsEnabled),
      distinctUntilChanged(),
    );
    this.notificationsEnabled$ = combineLatest([
      this.dataService.notificationsEnabled$,
      this.topbarService.hideNotifications$,
      vbcNotificationsEnabled$,
      onlyVBCAccess$,
    ]).pipe(
      map(
        ([dataNotificationsEnabled, hideNotifications, vbcNotificationsEnabled, onlyVBCAccess]: [
          boolean,
          boolean,
          boolean,
          boolean,
        ]) => {
          if (!dataNotificationsEnabled || hideNotifications) {
            return false;
          }
          if (onlyVBCAccess) {
            return vbcNotificationsEnabled;
          }
          return true;
        },
      ),
    );

    this.serviceProviderId$ = this.configService.serviceProviderId$;
    this.partnerId$ = this.configService.partnerId$;
    this.hideCenters$ = this.topbarService.hideCenters$;
    this.hideUserDropdown$ = combineLatest([
      this.topbarService.hideUserDropdown$,
      this.topbarService.dropdownItems$,
      this.dataService.email$,
    ]).pipe(
      map(([hideUserDropdown, dropdownItems, email]: [boolean, CustomUserDropdownItem[], string]) => {
        if (hideUserDropdown) {
          return true;
        }
        return !dropdownItems && !email;
      }),
    );
    this.showLanguageSelector$ = this.topbarService.showLanguageSelector$;
    this.dropdownItems$ = this.topbarService.dropdownItems$;

    this.userLanguageModalService.init();
  }

  propagateEvent(eventId: string): void {
    this.event.emit(eventId);
  }
}
